import { useEffect, useState } from "react";
import { UserPlus, Trash, EnvelopeSimple, Clock, Users, ArrowCircleUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const ROLES = [
  ["contract_administrator", "Contract Administrator"],
  ["grant_manager", "Grant Manager"],
  ["contract_specialist", "Contract Specialist"],
  ["legal_reviewer", "Legal Review"],
  ["leadership", "Executive Leadership"],
  ["admin", "Organization Admin"],
];

export default function TeamPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ members: [], pending_invites: [], billing: null });
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => setData((await api.get("/team/members")).data);
  useEffect(() => {
    load();
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const removeMember = async (id) => {
    if (!window.confirm("Remove this teammate?")) return;
    await api.delete(`/team/members/${id}`);
    toast.success("Teammate removed"); load();
  };
  const revokeInvite = async (id) => { await api.delete(`/team/invites/${id}`); toast.success("Invite revoked"); load(); };

  const b = data.billing;
  const full = b && b.seats_used >= b.seat_limit;
  const nextPlan = b ? plans.filter((p) => p.seats > b.seat_limit).sort((a, c) => a.seats - c.seats)[0] : null;

  const startUpgrade = async () => {
    if (!nextPlan) { toast.error("You're on the highest plan — contact us for Enterprise."); return; }
    const cycle = b?.billing_cycle === "yearly" ? "yearly" : "monthly";
    try {
      const { data: d } = await api.post("/billing/checkout", { lookup_key: `${nextPlan.key}_${cycle}`, origin_url: window.location.origin });
      window.location.href = d.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        overline="Organization"
        title="Team"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-btn"><UserPlus size={16} className="mr-1.5" /> Add teammate</Button>
            </DialogTrigger>
            <InviteDialog full={full} onSeatLimit={() => { setOpen(false); setUpgradeOpen(true); }} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      >
        Invite teammates into your workspace. Seats are set by your plan; grantee portal logins don't count.
      </PageHeader>

      {b && (
        <div className="mb-5 flex flex-wrap items-center gap-2 text-sm">
          <Users size={16} className="text-primary" />
          <span><b>{b.seats_used}</b> of {b.seat_limit >= 100000 ? "∞" : b.seat_limit} seats used on the {b.plan_name} plan.</span>
          {full && isAdmin && nextPlan && (
            <Button size="sm" className="h-7 bg-gold text-primary hover:bg-gold/90" data-testid="seat-upgrade-btn" onClick={() => setUpgradeOpen(true)}>
              <ArrowCircleUp size={15} className="mr-1" /> Upgrade to {nextPlan.name}
            </Button>
          )}
          {full && !isAdmin && <span className="text-alert">Seat limit reached — ask your admin to upgrade.</span>}
        </div>
      )}

      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="max-w-sm" data-testid="seat-upgrade-dialog">
          <DialogHeader><DialogTitle className="font-head">You've hit your seat limit</DialogTitle></DialogHeader>
          {nextPlan ? (
            <div className="text-sm text-muted-foreground">
              Your {b?.plan_name} plan includes {b?.seat_limit} seats. Upgrade to <b className="text-foreground">{nextPlan.name}</b> for
              {nextPlan.seats >= 100000 ? " unlimited seats" : ` up to ${nextPlan.seats} seats`} at <b className="text-foreground">${nextPlan.monthly}/mo</b> and keep adding teammates.
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">You're on our highest self-serve plan. Contact us for a tailored Enterprise plan.</div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUpgradeOpen(false)}>Not now</Button>
            {nextPlan && <Button data-testid="seat-upgrade-confirm" onClick={startUpgrade}>Upgrade to {nextPlan.name}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-3">
        {data.members.map((m) => (
          <div key={m.id} data-testid={`member-${m.id}`} className="bg-card border border-border rounded-md p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">{m.name?.[0] || "U"}</div>
              <div className="min-w-0">
                <div className="font-medium text-foreground truncate">{m.name} {m.id === user?.id && <span className="text-xs text-muted-foreground">(you)</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{m.email} · {m.role_label}</div>
              </div>
            </div>
            {m.id !== user?.id && (
              <Button size="sm" variant="ghost" data-testid={`member-remove-${m.id}`} className="text-alert hover:text-alert" onClick={() => removeMember(m.id)}>
                <Trash size={15} />
              </Button>
            )}
          </div>
        ))}
      </div>

      {data.pending_invites.length > 0 && (
        <div className="mt-8">
          <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><Clock size={14} /> Pending invites</div>
          <div className="space-y-2">
            {data.pending_invites.map((iv) => (
              <div key={iv.id} data-testid={`invite-${iv.id}`} className="bg-card border border-dashed border-border rounded-md p-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm"><EnvelopeSimple size={15} className="text-muted-foreground" /> {iv.email} · {iv.role}</div>
                <Button size="sm" variant="ghost" data-testid={`invite-revoke-${iv.id}`} onClick={() => revokeInvite(iv.id)} className="text-alert hover:text-alert">Revoke</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.members.length === 0 && <EmptyState title="No teammates yet" subtitle="Invite your first teammate to collaborate." />}
    </div>
  );
}

function InviteDialog({ full, onDone, onSeatLimit }) {
  const [tab, setTab] = useState("email");
  const [form, setForm] = useState({ name: "", email: "", role: "contract_administrator", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    if (tab === "direct" && (!form.password || form.password.length < 6)) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite", { name: form.name, email: form.email, role: form.role, mode: tab, password: tab === "direct" ? form.password : undefined });
      toast.success(data.invited ? "Invite email sent" : "Teammate added");
      onDone();
    } catch (e) {
      if (e.response?.status === 402) { onSeatLimit?.(); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle className="font-head">Add teammate</DialogTitle></DialogHeader>
      {full && <div className="text-xs text-alert bg-alert/10 rounded-md p-2">Seat limit reached — upgrade your plan in Billing before adding more.</div>}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="email" data-testid="invite-tab-email">Email invite</TabsTrigger>
          <TabsTrigger value="direct" data-testid="invite-tab-direct">Add directly</TabsTrigger>
        </TabsList>
        <div className="space-y-3 mt-4">
          <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="invite-name" value={form.name} onChange={set("name")} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="invite-email" value={form.email} onChange={set("email")} placeholder="teammate@company.com" /></div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <TabsContent value="direct" className="m-0">
            <div className="space-y-1.5"><Label>Temporary password</Label><Input data-testid="invite-password" type="text" value={form.password} onChange={set("password")} placeholder="Share securely" /></div>
          </TabsContent>
          <TabsContent value="email" className="m-0"><p className="text-xs text-muted-foreground">We'll email a secure link for them to set their own password.</p></TabsContent>
        </div>
      </Tabs>
      <DialogFooter>
        <Button data-testid="invite-submit" onClick={submit} disabled={busy}>{busy ? "Sending…" : tab === "email" ? "Send invite" : "Add teammate"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
